import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import LinkButton from '@/se/components/LinkButton';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocalPharmacyIcon from '@material-ui/icons/LocalPharmacy';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { setMedsDelivered, setExternalPharmacy, setUndoMedsDelivered, item } from '../../../../graph/patients';

const useStyles = makeStyles(theme => ({
  activeButton: {
    color: '#43be57',
    fontWeight: 600,
  },
  externalPharmacyButton: {
    color: '#ff9800',
    fontWeight: 600,
  },
  icon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1em',
  },
  optionButton: {
    margin: theme.spacing(1),
    minWidth: '200px',
  },
}));

const MedsDeliveredAction = ({ id, patient }) => {
  const classes = useStyles();
  const [openDialog, setOpenDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const refetchQueries = [
    {
      query: item,
      variables: { id: parseInt(id, 10) },
    },
  ];

  const [setMeds] = useMutation(setMedsDelivered, { refetchQueries });
  const [setExternal] = useMutation(setExternalPharmacy, { refetchQueries });
  const [undoMeds] = useMutation(setUndoMedsDelivered, { refetchQueries });

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelivered = async () => {
    setIsProcessing(true);
    try {
      await setMeds({ variables: { id: parseInt(id, 10) } });
      setOpenDialog(false);
    } catch (error) {
      console.error('Meds delivered failed', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExternalPharmacy = async () => {
    setIsProcessing(true);
    try {
      await setExternal({ variables: { id: parseInt(id, 10) } });
      setOpenDialog(false);
    } catch (error) {
      console.error('External pharmacy failed', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUndo = async () => {
    setIsProcessing(true);
    try {
      await undoMeds({ variables: { id: parseInt(id, 10) } });
      setOpenDialog(false);
    } catch (error) {
      console.error('Undo meds delivered failed', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const medsDeliveryStatus = patient?.medsDeliveryStatus;
  const isDelivered = medsDeliveryStatus === 'Delivered';
  const isExternalPharmacy = medsDeliveryStatus === 'ExternalPharmacy';
  const hasStatus = isDelivered || isExternalPharmacy;

  return (
    <>
      <LinkButton
        onClick={handleOpenDialog}
        disabled={isProcessing}
        className={isDelivered ? classes.activeButton : isExternalPharmacy ? classes.externalPharmacyButton : ''}
      >
        <Box display="flex" alignItems="center">
          {isDelivered ? (
            <>
              <CheckCircleIcon className={classes.icon} />
              Meds Delivered ✓
            </>
          ) : isExternalPharmacy ? (
            <>
              <CheckCircleIcon className={classes.icon} />
              Ext. Pharmacy ✓
            </>
          ) : (
            <>
              <LocalPharmacyIcon className={classes.icon} />
              Meds Delivered
            </>
          )}
        </Box>
      </LinkButton>

      <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="meds-delivered-dialog-title">
        <DialogTitle id="meds-delivered-dialog-title">
          {hasStatus ? 'Medication Delivery Status' : 'Set Medication Delivery'}
        </DialogTitle>
        <DialogContent>
          {hasStatus ? (
            <>
              <DialogContentText>
                Current status: <strong>{isDelivered ? 'Delivered' : 'External Pharmacy'}</strong>
              </DialogContentText>
              <DialogContentText>Do you want to clear this status?</DialogContentText>
            </>
          ) : (
            <DialogContentText>How are medications being delivered for this patient?</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {hasStatus ? (
            <>
              <Button onClick={handleCloseDialog} color="secondary" disabled={isProcessing}>
                Cancel
              </Button>
              <Button onClick={handleUndo} color="primary" disabled={isProcessing}>
                {isProcessing ? 'Processing...' : 'Clear Status'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} color="secondary" disabled={isProcessing}>
                Cancel
              </Button>
              <Button
                onClick={handleDelivered}
                variant="contained"
                className={classes.optionButton}
                disabled={isProcessing}
                style={{ backgroundColor: '#43be57', color: 'white' }}
              >
                {isProcessing ? 'Processing...' : 'Delivered (Internal)'}
              </Button>
              <Button
                onClick={handleExternalPharmacy}
                color="default"
                variant="contained"
                className={classes.optionButton}
                disabled={isProcessing}
                style={{ backgroundColor: '#ff9800', color: 'white' }}
              >
                {isProcessing ? 'Processing...' : 'External Pharmacy'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MedsDeliveredAction;
